/**
 * RegistrationContractSections -- THE ONE reusable registration field module
 * (REGISTRATION-PARTHA-CONTRACT-V1, ONE-REGISTRATION-MODULE law; founder ruling
 * 2026-08-13 "finish frontend unification").
 *
 * WHY THIS EXISTS: there is exactly ONE registration field/UI/validation module.
 * The appointment/check-in host (PatientIntakeMode) and the search/person-only
 * host both render THIS component for the governed Partha contract fields, so a
 * second field implementation cannot drift. Entry-point differences (prefill
 * source, persistence writer, chrome, post-save destination, and the primary
 * name/phone entry mode) live in the thin host adapter; the CONTRACT sections
 * (Title, Last name, Age/DOB, Gender, alternate-number owner, family relation,
 * address, occupation, emergency contact) live here, once.
 *
 * PRESENTATIONAL: this component owns NO writer, NO workflow, NO session. It
 * reads `values`, emits `onChange(patch)`, renders sections gated by
 * `isRequired(token)`, and draws vocabularies from `prefill`. The host derives
 * the age/minor/gender display bits and passes them in, so this module stays a
 * pure field surface.
 */

import { cloneElement, useRef, type JSX, type ReactNode } from 'react';
import type { SavedRegistrationContacts } from '@/dashboard/api';

import { SegmentedControl } from '@/dashboard/patients/RegisterPatientSteps';
import {
  ARRIVAL_GENDER_OPTIONS,
  ARRIVAL_LABEL_AGE,
  ARRIVAL_LABEL_DOB,
} from '@/dashboard/patients/register-wizard-model';
import { IntakeChoiceChips, IntakeQuestionSection } from './PatientIntakeScreens';
import {
  REGISTRATION_EMAIL_ON_FILE_LINE,
  REGISTRATION_EMAIL_POSTURE_LINE,
  REGISTRATION_GROUP_LABELS,
} from './registration-field-plan';
import { DOB_INPUT_PLACEHOLDER } from './patient-intake-mode-view';

/** The governed Partha registration field values -- the ONE shape both hosts
 *  adapt their own state into. */
export interface RegistrationFieldValues {
  readonly contactReuse?: string;
  readonly nameTitle: string;
  readonly lastName: string;
  readonly ageMode: 'age' | 'dob';
  readonly approxAge: string;
  readonly dob: string;
  readonly gender: string;
  /** PDC-FO-GAP-CLOSURE-V1 (G2): the optional email -- always rendered
   *  (COMPACT_OPTIONAL), never requirable (R-A). */
  readonly email: string;
  readonly altMobile: string;
  readonly altMobileRelation: string;
  readonly altMobileRelationName: string;
  readonly relationTitle: string;
  readonly relationName: string;
  readonly city: string;
  readonly addressArea: string;
  readonly state: string;
  readonly pincode: string;
  readonly addressDoor: string;
  readonly addressStreet: string;
  readonly addressLandmark: string;
  readonly addressTaluka: string;
  readonly addressCountry: string;
  readonly addressExpanded: boolean;
  readonly occupation: string;
  readonly handlingCategory: string;
  readonly emergencyContactName: string;
  readonly emergencyContactRelation: string;
  readonly emergencyContactPhone: string;
}

/** The vocabularies the sections render (the config-backed picklists +
 *  the state->district geography). Absent/empty vocab degrades to free text
 *  (R-017) so a required field stays completable in every config state. */
export interface RegistrationSectionsPrefill {
  readonly nameTitleOptions?: readonly string[];
  readonly relationOptions?: readonly string[];
  readonly relationTitleOptions?: readonly string[];
  readonly occupationOptions?: readonly string[];
  readonly handlingCategories?: readonly string[];
  readonly stateDistricts?: Readonly<Record<string, readonly string[]>>;
}

export interface RegistrationContractSectionsProps {
  /** Approved desk form; tablet/intake keeps its established choice controls. */
  readonly visitFields?: ReactNode;
  readonly extraAddressFields?: ReactNode;
  readonly choicePresentation?: 'chips' | 'select';
  readonly values: RegistrationFieldValues;
  readonly onChange: (patch: Partial<RegistrationFieldValues>) => void;
  /** Renders a section only when the tenant contract names its canonical key
   *  (config-gated; the COMPLETABILITY-INVARIANT counts one control per key). */
  readonly isRequired: (token: string) => boolean;
  readonly prefill: RegistrationSectionsPrefill | null;
  readonly busy?: boolean;
  /** Host-derived display bits (kept out of this presentational module). */
  readonly ageTitle: string;
  readonly ageDisplay: string | null;
  readonly minorNote: string | null;
  readonly genderRequired: boolean;
  readonly genderHint: string | null;
  /** DESK identity-as-fields slots (REGISTRATION-DEPLOYED-WALK-CLOSURE-V1,
   *  founder visual verdict 2026-08-13). The desk modal IS the mockup contract:
   *  identity renders as FIELDS inside the section cards. The host passes the
   *  Patient-details eyebrow + the prefilled Full name field (slotted after
   *  Title, matching the mockup's First-name position) + the Mobile field
   *  (slotted first in Contact). The patient tablet + the person-only wizard
   *  pass NONE and keep their own identity composition (unified grid off). */
  readonly patientSectionTitle?: string | null;
  readonly workspaceRegistration?: boolean;
  readonly patientIdentityField?: ReactNode;
  readonly contactMobileField?: ReactNode;
  /** SHIPPED-WORK-TRUTH-CORRECTIVE-V1 (T4): TRUE when the reopened
   *  registration already carries a saved email -- the section then says so
   *  (presence line) instead of presenting a silently blank field. The
   *  VALUE never re-rides the staff wire (D4 posture); hosts without a
   *  presence read pass nothing (absent = the base posture line only). */
  readonly emailOnFile?: boolean;
  /** Verified saved-capture readback only; absent keeps the editable form. */
  readonly savedContactFields?: SavedRegistrationContacts | null;
}

/**
 * The governed registration contract sections (Title through Emergency). The
 * host renders the identity read-back / primary phone above this, and the
 * reason / consent / submit chrome below it.
 */
export function RegistrationContractSections({
  visitFields, extraAddressFields,
  choicePresentation = 'chips',
  values,
  onChange: onFieldChange,
  isRequired,
  prefill,
  busy = false,
  ageTitle,
  ageDisplay,
  minorNote,
  genderRequired,
  genderHint,
  patientSectionTitle,
  workspaceRegistration = false,
  patientIdentityField,
  contactMobileField,
  emailOnFile = false,
  savedContactFields,
}: RegistrationContractSectionsProps): JSX.Element {
  const separateContacts = useRef<Partial<RegistrationFieldValues> | null>(null);
  const onChange = (patch: Partial<RegistrationFieldValues>): void => {
    const changedEmergency = ['emergencyContactName', 'emergencyContactRelation', 'emergencyContactPhone']
      .some(key => key in patch && patch[key as keyof RegistrationFieldValues] !== values[key as keyof RegistrationFieldValues]);
    onFieldChange(workspaceRegistration && values.contactReuse === 'same' && changedEmergency
      ? {...patch, contactReuse: ''} : patch);
  };
  const districtsForState =
    (prefill?.stateDistricts ?? {})[values.state.trim().toUpperCase()] ?? [];

  // The Patient-details fields as standalone elements so the layout can be
  // composed two ways WITHOUT duplicating field JSX: the DESK unified mockup
  // grid (Title, Full name, [Last name when contract-named], Age/DOB,
  // Gender-span) when the host supplies its identity field, and the legacy
  // two-grid split (Title[|Last name], Age/DOB|Gender) for the tablet +
  // person-only wizard.
  const titleField = (
    <IntakeQuestionSection
      id="pim-sec-name-title"
      title="Title"
      required={isRequired('name_title')}
      answered={values.nameTitle.trim() !== ''}
      testId="intake-name-title"
      fieldKey="name_title"
    >
      {(prefill?.nameTitleOptions ?? []).length > 0 ? (
        <select
          className="pim-select"
          aria-label="Title"
          value={values.nameTitle}
          disabled={busy}
          onChange={(e) => onChange({ nameTitle: e.target.value })}
          data-testid="intake-name-title-select"
        >
          <option value="">{workspaceRegistration ? 'Choose...' : 'Select title'}</option>
          {(prefill?.nameTitleOptions ?? []).map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      ) : (
        <input
          className="pim-input"
          aria-label="Title"
          value={values.nameTitle}
          disabled={busy}
          onChange={(e) => onChange({ nameTitle: e.target.value })}
          data-testid="intake-name-title-input"
        />
      )}
    </IntakeQuestionSection>
  );
  // REGISTRATION-FIELD-CONTROLS-V1 (founder walk order 2026-08-13 evening):
  // Full name is the ONE receptionist-facing patient-name entry, so the
  // Last-name control is CONFIG-GATED (renders only when the tenant contract
  // names last_name) instead of always-rendered -- the consumer audit proved
  // the field load-bearing nowhere (no search / identity / UMR / report
  // reader). Storage, writers, and the requirable token stand; a stored
  // legacy value still round-trips invisibly through the host state.
  const lastNameField = isRequired('last_name') ? (
    <IntakeQuestionSection
      id="pim-sec-last-name"
      title="Last name"
      required
      answered={values.lastName.trim() !== ''}
      testId="intake-last-name"
      fieldKey="last_name"
    >
      <input
        className="pim-input"
        aria-label="Last name"
        value={values.lastName}
        disabled={busy}
        onChange={(e) => onChange({ lastName: e.target.value })}
        data-testid="intake-last-name-input"
      />
    </IntakeQuestionSection>
  ) : null;
  const ageField = (
    <IntakeQuestionSection
      id="pim-sec-dob"
      title={workspaceRegistration ? 'Age / DOB' : ageTitle}
      required={isRequired('age_or_dob')}
      answered={ageDisplay !== null}
      fieldKey="age_or_dob"
    >
      <div data-testid="intake-age-toggle">
        {choicePresentation === 'select' && !workspaceRegistration ? <select className="pim-select" aria-label="Age or date of birth"
          value={values.ageMode} disabled={busy}
          onChange={event => onChange({ageMode: event.target.value as 'age' | 'dob'})}>
          <option value="age">{ARRIVAL_LABEL_AGE}</option>
          <option value="dob">{ARRIVAL_LABEL_DOB}</option>
        </select> : (
        <SegmentedControl
          ariaLabel="Age or date of birth"
          options={[
            { value: 'age', label: ARRIVAL_LABEL_AGE },
            { value: 'dob', label: workspaceRegistration ? 'DOB' : ARRIVAL_LABEL_DOB },
          ]}
          value={values.ageMode}
          disabled={busy}
          onPick={(v) => onChange({ ageMode: v as 'age' | 'dob' })}
        />
        )}
      </div>
      {workspaceRegistration ? <p className="pim-age-input-label">{values.ageMode === 'dob' ? 'Date of birth' : 'Age in years'}{isRequired('age_or_dob') ? ' *' : ''}</p> : null}
      {values.ageMode === 'dob' ? (
        <input
          className="pim-input"
          aria-label="Date of birth (dd/mm/yyyy)"
          inputMode="numeric"
          placeholder={DOB_INPUT_PLACEHOLDER}
          value={values.dob}
          onChange={(e) => onChange({ dob: e.target.value })}
          style={{ marginTop: 'var(--ac-space-2)' }}
          data-testid="intake-dob-input"
        />
      ) : (
        <input
          className="pim-input"
          aria-label="Approximate age"
          inputMode="numeric"
          placeholder={workspaceRegistration ? 'Years' : 'Your age in years'}
          value={values.approxAge}
          onChange={(e) => onChange({ approxAge: e.target.value })}
          style={{ marginTop: 'var(--ac-space-2)' }}
        />
      )}
      {!workspaceRegistration && ageDisplay !== null ? (
        <p
          className="pim-hint"
          style={{ margin: 'var(--ac-space-3) 0 0' }}
          data-testid="derived-age"
        >
          Age: {ageDisplay}
        </p>
      ) : null}
      {minorNote !== null ? (
        <p className="pim-minor" data-testid="intake-minor-note">
          {minorNote}
        </p>
      ) : null}
    </IntakeQuestionSection>
  );
  const genderField = (
    <IntakeQuestionSection
      id="pim-sec-gender"
      title="Gender"
      required={genderRequired}
      answered={values.gender.trim() !== ''}
      fieldKey="gender"
      hint={genderHint}
    >
      {/* Gender is a 'chips' control (registration-field-plan): the module
          renders it through the shared IntakeChoiceChips primitive (the ONE
          owner of the ac-tristate button element, in the grandfathered
          PatientIntakeScreens) -- never a re-hand-rolled raw button
          (design-system conformance RULE 3). Byte-identical markup:
          ac-tristate group, ac-tristate__option[--on], aria-pressed, and
          the intake-gender-<value> per-chip testid. */}
      {choicePresentation === 'select' ? <select className="pim-select" aria-label="Gender"
        value={values.gender} disabled={busy} onChange={event => onChange({gender:event.target.value})}>
        <option value="">{workspaceRegistration ? 'Choose...' : 'Choose gender'}</option>
        {ARRIVAL_GENDER_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
        {values.gender !== '' && !ARRIVAL_GENDER_OPTIONS.some(option => option.value === values.gender) ? <option value={values.gender}>{values.gender}</option> : null}
      </select> : (
      <IntakeChoiceChips
        ariaLabel="Gender"
        options={ARRIVAL_GENDER_OPTIONS.map((g) => ({
          code: g.value,
          label: g.label,
        }))}
        isSelected={(code) => values.gender === code}
        onPick={(code) => onChange({ gender: code })}
        disabled={busy}
        testId="intake-gender"
      />
      )}
    </IntakeQuestionSection>
  );

  const contractFields = {
email: (<IntakeQuestionSection
              id="pim-sec-email"
              title={workspaceRegistration && !isRequired('email') ? 'Email · optional' : 'Email'}
              required={isRequired('email')}
              answered={values.email.trim() !== ''}
              testId="intake-email"
              fieldKey="email"
              hint={workspaceRegistration ? 'Optional.' : REGISTRATION_EMAIL_POSTURE_LINE}
            >
              <input
                className="pim-input"
                aria-label="Email"
                type="email"
                inputMode="email"
                value={values.email}
                disabled={busy}
                onChange={(e) => onChange({ email: e.target.value })}
                data-testid="intake-email-input"
              />
              {emailOnFile && values.email.trim() === '' ? (
                <p className="pim-hint" data-testid="intake-email-on-file">
                  {REGISTRATION_EMAIL_ON_FILE_LINE}
                </p>
              ) : null}
            </IntakeQuestionSection>),
alt_mobile: (<IntakeQuestionSection
                id="pim-sec-alt-mobile"
                title={workspaceRegistration ? 'Alternate mobile' : 'Alternate phone'}
                required
                answered={values.altMobile.trim() !== ''}
                testId="intake-alt-mobile"
                fieldKey="alt_mobile"
              >
                <input
                  className="pim-input"
                  aria-label={workspaceRegistration ? 'Alternate mobile' : 'Alternate phone'}
                  type="tel"
                  inputMode="tel"
                  value={values.altMobile}
                  disabled={busy}
                  onChange={(e) => onChange({ altMobile: e.target.value })}
                  data-testid="intake-alt-mobile-input"
                />
              </IntakeQuestionSection>),
alt_mobile_relation: (<IntakeQuestionSection
                id="pim-sec-alt-mobile-relation"
                title={workspaceRegistration ? 'Relationship' : 'Alternate phone relation'}
                required
                answered={values.altMobileRelation.trim() !== ''}
                testId="intake-alt-mobile-relation"
                fieldKey="alt_mobile_relation"
              >
                {(prefill?.relationOptions ?? []).length > 0 ? (
                  <select
                    className="pim-select"
                    aria-label={workspaceRegistration ? 'Relationship' : 'Alternate phone relation'}
                    value={values.altMobileRelation}
                    disabled={busy}
                    onChange={(e) =>
                      onChange({ altMobileRelation: e.target.value })
                    }
                    data-testid="intake-alt-mobile-relation-select"
                  >
                    <option value="">{workspaceRegistration ? 'Choose...' : 'Select relation'}</option>
                    {(prefill?.relationOptions ?? []).map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    className="pim-input"
                    aria-label={workspaceRegistration ? 'Relationship' : 'Alternate phone relation'}
                    value={values.altMobileRelation}
                    disabled={busy}
                    onChange={(e) =>
                      onChange({ altMobileRelation: e.target.value })
                    }
                    data-testid="intake-alt-mobile-relation-input"
                  />
                )}
              </IntakeQuestionSection>),
alt_mobile_relation_name: (<IntakeQuestionSection
                id="pim-sec-alt-mobile-relation-name"
                title={workspaceRegistration ? 'Alternate contact name' : 'Alternate phone relation name'}
                required
                answered={values.altMobileRelationName.trim() !== ''}
                testId="intake-alt-mobile-relation-name"
                fieldKey="alt_mobile_relation_name"
              >
                <input
                  className="pim-input"
                  aria-label={workspaceRegistration ? 'Alternate contact name' : 'Alternate phone relation name'}
                  value={values.altMobileRelationName}
                  disabled={busy}
                  onChange={(e) =>
                    onChange({ altMobileRelationName: e.target.value })
                  }
                  data-testid="intake-alt-mobile-relation-name-input"
                />
              </IntakeQuestionSection>),
relation_title: (<IntakeQuestionSection
                id="pim-sec-relation-title"
                title={workspaceRegistration ? 'Family title' : 'Relation title'}
                required
                answered={values.relationTitle.trim() !== ''}
                testId="intake-relation-title"
                fieldKey="relation_title"
              >
                {(prefill?.relationTitleOptions ?? []).length > 0 ? (
                  <select
                    className="pim-select"
                    aria-label="Relation title"
                    value={values.relationTitle}
                    disabled={busy}
                    onChange={(e) =>
                      onChange({ relationTitle: e.target.value })
                    }
                    data-testid="intake-relation-title-select"
                  >
                    <option value="">{workspaceRegistration ? 'Choose...' : 'Select relation'}</option>
                    {(prefill?.relationTitleOptions ?? []).map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    className="pim-input"
                    aria-label="Relation title"
                    value={values.relationTitle}
                    disabled={busy}
                    onChange={(e) =>
                      onChange({ relationTitle: e.target.value })
                    }
                    data-testid="intake-relation-title-input"
                  />
                )}
              </IntakeQuestionSection>),
relation_name: (<IntakeQuestionSection
                id="pim-sec-relation"
                title={
                  workspaceRegistration
                    ? 'Family name on record'
                    : isRequired('relation_title')
                    ? 'Relation name'
                    : 'Parent / spouse / guardian name'
                }
                required
                answered={values.relationName.trim() !== ''}
                testId="intake-relation"
                fieldKey="relation_name"
              >
                <input
                  className="pim-input"
                  aria-label={
                    workspaceRegistration
                    ? 'Family name on record'
                    : isRequired('relation_title')
                      ? 'Relation name'
                      : 'Parent / spouse / guardian name'
                  }
                  value={values.relationName}
                  disabled={busy}
                  onChange={(e) => onChange({ relationName: e.target.value })}
                  data-testid="intake-relation-input"
                />
              </IntakeQuestionSection>),
state: (<IntakeQuestionSection
          id="pim-sec-state"
          title="State"
          required={isRequired('state')}
          answered={values.state.trim() !== ''}
          testId="intake-state"
          fieldKey="state"
        >
          <input
            className="pim-input"
            aria-label="State"
            value={values.state}
            disabled={busy}
            onChange={(e) => onChange({ state: e.target.value })}
            data-testid="intake-state-input"
          />
        </IntakeQuestionSection>),
city: (<IntakeQuestionSection
            id="pim-sec-city"
            title="City / district"
            required
            answered={values.city.trim() !== ''}
            testId="intake-city"
            fieldKey="city"
            hint={
              values.state.trim() !== '' && districtsForState.length === 0
                ? 'District list for this state is coming soon - type the district name.'
                : null
            }
          >
            <input
              className="pim-input"
              aria-label="City / district"
              list="intake-city-districts"
              value={values.city}
              disabled={busy}
              onChange={(e) => onChange({ city: e.target.value })}
              data-testid="intake-city-input"
            />
            <datalist id="intake-city-districts">
              {districtsForState.map((d) => (
                <option key={d} value={d} />
              ))}
            </datalist>
          </IntakeQuestionSection>),
address_area: (<IntakeQuestionSection
            id="pim-sec-address-area"
            title="Area / town / village"
            required
            answered={values.addressArea.trim() !== ''}
            testId="intake-address-area"
            fieldKey="address_area"
          >
            <input
              className="pim-input"
              aria-label="Area / town / village"
              value={values.addressArea}
              disabled={busy}
              onChange={(e) => onChange({ addressArea: e.target.value })}
              data-testid="intake-area-input"
            />
          </IntakeQuestionSection>),
pincode: (<IntakeQuestionSection
          id="pim-sec-pincode"
          title="Pincode"
          required={isRequired('pincode')}
          answered={values.pincode.trim() !== ''}
          testId="intake-pincode"
          fieldKey="pincode"
        >
          <input
            className="pim-input"
            aria-label="Pincode"
            inputMode="numeric"
            value={values.pincode}
            disabled={busy}
            onChange={(e) => onChange({ pincode: e.target.value })}
            data-testid="intake-pincode-input"
          />
        </IntakeQuestionSection>),
address_door: (<IntakeQuestionSection
            id="pim-sec-address-door"
            title="Door no / building"
            required={isRequired('address_door')}
            answered={values.addressDoor.trim() !== ''}
            testId="intake-address-door"
            fieldKey="address_door"
          >
            <input
              className="pim-input"
              aria-label="Door no / building"
              value={values.addressDoor}
              disabled={busy}
              onChange={(e) => onChange({ addressDoor: e.target.value })}
              data-testid="intake-door-input"
            />
          </IntakeQuestionSection>),
address_street: (<IntakeQuestionSection
            id="pim-sec-address-street"
            title="Street"
            required={isRequired('address_street')}
            answered={values.addressStreet.trim() !== ''}
            testId="intake-address-street"
            fieldKey="address_street"
          >
            <input
              className="pim-input"
              aria-label="Street"
              value={values.addressStreet}
              disabled={busy}
              onChange={(e) => onChange({ addressStreet: e.target.value })}
              data-testid="intake-street-input"
            />
          </IntakeQuestionSection>),
address_area_expanded: (<IntakeQuestionSection
              id="pim-sec-address-area"
              title="Area / town / village"
              answered={values.addressArea.trim() !== ''}
              testId="intake-address-area"
              fieldKey="address_area"
            >
              <input
                className="pim-input"
                aria-label="Area / town / village"
                value={values.addressArea}
                disabled={busy}
                onChange={(e) => onChange({ addressArea: e.target.value })}
                data-testid="intake-area-input"
              />
            </IntakeQuestionSection>),
address_landmark: (<IntakeQuestionSection
            id="pim-sec-address-landmark"
            title="Landmark"
            required={isRequired('address_landmark')}
            answered={values.addressLandmark.trim() !== ''}
            testId="intake-address-landmark"
            fieldKey="address_landmark"
          >
            <input
              className="pim-input"
              aria-label="Landmark"
              value={values.addressLandmark}
              disabled={busy}
              onChange={(e) => onChange({ addressLandmark: e.target.value })}
              data-testid="intake-landmark-input"
            />
          </IntakeQuestionSection>),
address_taluka: (<IntakeQuestionSection
            id="pim-sec-address-taluka"
            title="Taluka"
            required={isRequired('address_taluka')}
            answered={values.addressTaluka.trim() !== ''}
            testId="intake-address-taluka"
            fieldKey="address_taluka"
          >
            <input
              className="pim-input"
              aria-label="Taluka"
              value={values.addressTaluka}
              disabled={busy}
              onChange={(e) => onChange({ addressTaluka: e.target.value })}
              data-testid="intake-taluka-input"
            />
          </IntakeQuestionSection>),
address_country: (<IntakeQuestionSection
            id="pim-sec-address-country"
            title="Country"
            required={isRequired('address_country')}
            answered={values.addressCountry.trim() !== ''}
            testId="intake-address-country"
            fieldKey="address_country"
          >
            <input
              className="pim-input"
              aria-label="Country"
              value={values.addressCountry}
              disabled={busy}
              onChange={(e) => onChange({ addressCountry: e.target.value })}
              data-testid="intake-country-input"
            />
          </IntakeQuestionSection>),
occupation: (<IntakeQuestionSection
          id="pim-sec-occupation"
          title="Occupation"
          required
          answered={values.occupation.trim() !== ''}
          testId="intake-occupation"
          fieldKey="occupation"
        >
          {(prefill?.occupationOptions ?? []).length > 0 ? (
            <select
              className="pim-select"
              aria-label="Occupation"
              value={values.occupation}
              disabled={busy}
              onChange={(e) => onChange({ occupation: e.target.value })}
              data-testid="intake-occupation-select"
            >
              <option value="">{workspaceRegistration ? 'Choose...' : 'Select occupation'}</option>
              {(prefill?.occupationOptions ?? []).map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          ) : (
            <input
              className="pim-input"
              aria-label="Occupation"
              value={values.occupation}
              disabled={busy}
              onChange={(e) => onChange({ occupation: e.target.value })}
              data-testid="intake-occupation-input"
            />
          )}
        </IntakeQuestionSection>),
handling_category: (<IntakeQuestionSection
          id="pim-sec-handling"
          title="Visit category"
          required
          answered={values.handlingCategory.trim() !== ''}
          testId="intake-handling"
          fieldKey="handling_category"
        >
          {/* Visit category is a 'chips' control (registration-field-plan):
              same shared IntakeChoiceChips primitive as gender -- the vocabulary
              renders verbatim (RULING 3), one chip per configured category, with
              the intake-handling-<category> per-chip testid. No raw button here
              (design-system conformance RULE 3). */}
          <IntakeChoiceChips
            ariaLabel="Visit category"
            options={(prefill?.handlingCategories ?? []).map((c) => ({
              code: c,
              label: c,
            }))}
            isSelected={(code) => values.handlingCategory === code}
            onPick={(code) => onChange({ handlingCategory: code })}
            disabled={busy}
            testId="intake-handling"
          />
        </IntakeQuestionSection>),
emergency_contact_name: (<IntakeQuestionSection
                id="pim-sec-emergency-name"
                title={workspaceRegistration ? 'Contact name' : 'Emergency contact name'}
                required
                answered={values.emergencyContactName.trim() !== ''}
                testId="intake-emergency-name"
                fieldKey="emergency_contact_name"
              >
                <input
                  className="pim-input"
                  aria-label="Emergency contact name"
                  value={values.emergencyContactName}
                  disabled={busy}
                  onChange={(e) =>
                    onChange({ emergencyContactName: e.target.value })
                  }
                  data-testid="intake-emergency-name-input"
                />
              </IntakeQuestionSection>),
emergency_contact_relation: (<IntakeQuestionSection
                id="pim-sec-emergency-relation"
                title={workspaceRegistration ? 'Relationship' : 'Emergency contact relation'}
                required
                answered={values.emergencyContactRelation.trim() !== ''}
                testId="intake-emergency-relation"
                fieldKey="emergency_contact_relation"
              >
                {(prefill?.relationOptions ?? []).length > 0 ? (
                  <select
                    className="pim-select"
                    aria-label="Emergency contact relation"
                    value={values.emergencyContactRelation}
                    disabled={busy}
                    onChange={(e) =>
                      onChange({ emergencyContactRelation: e.target.value })
                    }
                    data-testid="intake-emergency-relation-select"
                  >
                    <option value="">{workspaceRegistration ? 'Choose...' : 'Select relation'}</option>
                    {(prefill?.relationOptions ?? []).map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    className="pim-input"
                    aria-label="Emergency contact relation"
                    value={values.emergencyContactRelation}
                    disabled={busy}
                    onChange={(e) =>
                      onChange({ emergencyContactRelation: e.target.value })
                    }
                    data-testid="intake-emergency-relation-input"
                  />
                )}
              </IntakeQuestionSection>),
emergency_contact_phone: (<IntakeQuestionSection
                id="pim-sec-emergency-phone"
                title={workspaceRegistration ? 'Contact mobile' : 'Emergency contact phone'}
                required
                answered={values.emergencyContactPhone.trim() !== ''}
                testId="intake-emergency-phone"
                fieldKey="emergency_contact_phone"
              >
                <input
                  className="pim-input"
                  aria-label="Emergency contact phone"
                  type="tel"
                  inputMode="tel"
                  value={values.emergencyContactPhone}
                  disabled={busy}
                  onChange={(e) =>
                    onChange({ emergencyContactPhone: e.target.value })
                  }
                  data-testid="intake-emergency-phone-input"
                />
              </IntakeQuestionSection>),
  };
  const chooseContactReuse = (choice: string): void => {
    if (choice === values.contactReuse) return;
    if (choice === 'same') {
      separateContacts.current ??= { relationName: values.relationName,
        altMobile: values.altMobile, altMobileRelation: values.altMobileRelation,
        altMobileRelationName: values.altMobileRelationName };
      // A deliberate copy, never a live binding or an inferred family title.
      onChange({contactReuse: choice, relationName: values.emergencyContactName,
        altMobile: values.emergencyContactPhone,
        altMobileRelation: values.emergencyContactRelation,
        altMobileRelationName: values.emergencyContactName});
    } else {
      onChange({...separateContacts.current, contactReuse: choice});
      separateContacts.current = null;
    }
  };
  const field = (key: keyof typeof contractFields): ReactNode => {
    if (savedContactFields && key in savedContactFields) {
      const saved = savedContactFields[key as keyof SavedRegistrationContacts];
      const text = saved.state === 'saved' ? saved.value : saved.state === 'withheld'
        ? 'Saved — hidden in this view' : saved.state === 'not_supplied' ? 'Not supplied' : 'Saved details unavailable';
      return cloneElement(contractFields[key], {required:false,answered:saved.state === 'saved' || saved.state === 'withheld'},
        <p className="pim-review__v" data-testid={`intake-saved-${key}`} data-state={saved.state}>{text}</p>);
    }
    return isRequired(key) ? contractFields[key] : null;
  };
  if (workspaceRegistration) return <div className="arrival-registration-fields">
    <section className="pim-section" data-testid="reg-group-patient-details">
      <h2 className="pim-section__title">Patient</h2>
      <div className="arrival-identity-grid">{titleField}{patientIdentityField}{ageField}{genderField}{lastNameField}</div>
      <div className="arrival-contact-grid">{contactMobileField}{contractFields.email}{field('occupation')}</div>
    </section>
    <section className="pim-section arrival-family-section">
      <h2 className="pim-section__title">Family &amp; emergency contact</h2>
      <div className="arrival-contact-grid">{field('emergency_contact_name')}{field('emergency_contact_relation')}{field('emergency_contact_phone')}</div>
      {savedContactFields ? <>
        <div className="arrival-contact-grid">{field('relation_title')}{field('relation_name')}</div>
        <div className="arrival-contact-grid">{field('alt_mobile_relation_name')}{field('alt_mobile_relation')}{field('alt_mobile')}</div>
      </> : <><div className="arrival-reuse-row"><div>
        <p id="registration-contact-reuse-label">Family member and alternate contact are the same as the emergency contact? *</p>
        <SegmentedControl ariaLabel="Family member and alternate contact are the same as the emergency contact?"
          options={[{value:'same',label:'Yes'},{value:'different',label:'No'}]}
          value={values.contactReuse ?? ''} onPick={chooseContactReuse} disabled={busy}/>
      </div>{field('relation_title')}</div>
      {values.contactReuse === 'same' ? <p className="pim-hint" role="status">Copied from the emergency contact. Family title stays separate.</p> : null}
      {values.contactReuse === 'different' ? <div className="arrival-separate-contacts">
        {field('relation_name')}<div className="arrival-contact-grid">{field('alt_mobile_relation_name')}{field('alt_mobile_relation')}{field('alt_mobile')}</div>
      </div> : null}</>}
    </section>
    <section className="pim-section">
      <h2 className="pim-section__title">Address &amp; visit</h2>
      <div className="arrival-address-grid">{contractFields.state}{field('city')}{field('address_area')}{contractFields.pincode}</div>
      {field('handling_category')}
      {visitFields ? <div className="arrival-contact-grid">{visitFields}</div> : null}
      <details open={values.addressExpanded} onToggle={e=>onChange({addressExpanded:e.currentTarget.open})}>
        <summary>More address details · optional</summary><div className="arrival-contact-grid">
        {!isRequired('address_area') ? contractFields.address_area_expanded : null}
          {contractFields.address_door}{contractFields.address_street}{contractFields.address_landmark}{contractFields.address_taluka}{contractFields.address_country}{extraAddressFields}
        </div>
      </details>
    </section>
  </div>;
  return (
    <>
      {/* REGISTRATION-DEPLOYED-WALK-CLOSURE-V1 (Defect B1 + founder visual
          verdict 2026-08-13): each governed group is ONE section card matching
          the founder-approved mockup
          (docs/design/mockups/registration-conform-2026-08-12.html). The
          Patient details card is composed TWO ways from the same field elements
          (defined above, so no field JSX is duplicated):
          - DESK modal (identityLayout 'fields'): the UNIFIED mockup grid --
            eyebrow + Title, Full name, Last name, Age/DOB, Gender(span) -- with
            the host's prefilled Full name field slotted after Title (the
            mockup's First-name position; repo truth is the canonical full_name,
            not a separate first name -- see the run record's B6 table).
          - Patient tablet + person-only wizard: the legacy headerless two-grid
            split (Title|Last name, Age/DOB|Gender), unchanged, above their own
            identity composition. */}
      <section className="pim-section" data-testid="reg-group-patient-details">
        {patientSectionTitle !== undefined && patientSectionTitle !== null ? (
          <>
            {patientSectionTitle ? (
              <h2
                className="pim-section__title"
                data-testid="reg-group-patient-details-title"
              >
                {patientSectionTitle}
              </h2>
            ) : null}
            <div className="pim-grid">
              {titleField}
              {patientIdentityField}
              {lastNameField}
              {ageField}
              <div className="pim-qfield--span">{genderField}</div>
            </div>
          </>
        ) : (
          <>
            {/* Title (+ Last name only when the tenant contract names it);
                requiredness follows the tenant contract. */}
            <div className="pim-grid">
              {titleField}
              {lastNameField}
            </div>
            {/* age/DOB and gender sit side by side at desktop width. */}
            <div className="pim-grid">
              {ageField}
              {genderField}
            </div>
          </>
        )}
      </section>

      {/* CONTACT (Part C): the person's Mobile (DESK slot) + the alternate
          number + its owner + the optional Email (G2, PDC-FO-GAP-CLOSURE-V1:
          COMPACT_OPTIONAL -- always rendered, never mandatory, so this
          section now renders on EVERY door). */}
      <section className="pim-section">
          <h2 className="pim-section__title" data-testid="reg-group-contact">
            {REGISTRATION_GROUP_LABELS.contact}
          </h2>
          <div className="pim-grid">
            {/* DESK: the person's OWN Mobile, prefilled + editable, leads Contact
                (the mockup's Mobile position). The tablet/wizard pass none. */}
            {contactMobileField}
            {/* PDC-FO-GAP-CLOSURE-V1 (G2, founder ruling 2026-08-14): the
                OPTIONAL email -- validated at submit through THE ONE
                parseContractEmail (both writers 400 identically on junk);
                stored on the golden record at mint. Never requirable
                (R-A/FS-12), so `required` is honestly always false.
                T4 (SHIPPED-WORK-TRUTH-CORRECTIVE-V1): the POSTURE LINE says
                where the email goes, and a reopened door with a saved email
                SAYS SO (presence only -- the value never re-rides the
                staff wire, the D4 posture). */}
            {contractFields.email}
            {isRequired('alt_mobile') ? (contractFields.alt_mobile) : null}
            {isRequired('alt_mobile_relation') ? (contractFields.alt_mobile_relation) : null}
            {isRequired('alt_mobile_relation_name') ? (contractFields.alt_mobile_relation_name) : null}
          </div>
      </section>

      {/* FAMILY: relation title + name (config-gated). */}
      {isRequired('relation_name') || isRequired('relation_title') ? (
        <section className="pim-section">
          <h2 className="pim-section__title" data-testid="reg-group-family">
            {REGISTRATION_GROUP_LABELS.family}
          </h2>
          <div className="pim-grid">
            {isRequired('relation_title') ? (contractFields.relation_title) : null}
            {isRequired('relation_name') ? (contractFields.relation_name) : null}
          </div>
        </section>
      ) : null}

      {/* ADDRESS: city/district (validated typeahead) + area + state + pincode,
          with the optional full-address tail behind an expander. */}
      <section className="pim-section">
      <h2 className="pim-section__title" data-testid="reg-group-address">
        {REGISTRATION_GROUP_LABELS.address}
      </h2>
      <div className="pim-grid">
        {/* State FIRST so the City/district typeahead cascades from it -- the
            founder-approved mockup's Address field order (State -> City). */}
        {contractFields.state}
        {isRequired('city') ? (contractFields.city) : null}
        {isRequired('address_area') ? (contractFields.address_area) : null}
        {contractFields.pincode}
      </div>

      <details
        className="pim-card pim-qcard"
        open={values.addressExpanded}
        onToggle={(e) =>
          onChange({ addressExpanded: (e.target as HTMLDetailsElement).open })
        }
        data-testid="intake-address-expander"
      >
        <summary className="pim-q">Add full address</summary>
        <div className="pim-grid">
          {contractFields.address_door}
          {contractFields.address_street}
          {isRequired('address_area') ? null : (contractFields.address_area_expanded)}
          {contractFields.address_landmark}
          {contractFields.address_taluka}
          {contractFields.address_country}{extraAddressFields}
        </div>
      </details>
      </section>

      {/* OTHER DETAILS: occupation + visit category (config-gated). */}
      {isRequired('occupation') ||
      (isRequired('handling_category') &&
        (prefill?.handlingCategories ?? []).length > 0) ? (
        <section className="pim-section">
          <h2 className="pim-section__title" data-testid="reg-group-other">
            {REGISTRATION_GROUP_LABELS.other}
          </h2>
          <div className="pim-grid">
            {isRequired('occupation') ? (contractFields.occupation) : null}
      {isRequired('handling_category') &&
      (prefill?.handlingCategories ?? []).length > 0 ? (contractFields.handling_category) : null}
          </div>
        </section>
      ) : null}

      {/* EMERGENCY CONTACT (Part D): name + relation + phone (config-gated). */}
      {isRequired('emergency_contact_name') ||
      isRequired('emergency_contact_relation') ||
      isRequired('emergency_contact_phone') ? (
        <section className="pim-section">
          <h2 className="pim-section__title" data-testid="reg-group-emergency">
            {REGISTRATION_GROUP_LABELS.emergency}
          </h2>
          <div className="pim-grid">
            {isRequired('emergency_contact_name') ? (contractFields.emergency_contact_name) : null}
            {isRequired('emergency_contact_relation') ? (contractFields.emergency_contact_relation) : null}
            {isRequired('emergency_contact_phone') ? (contractFields.emergency_contact_phone) : null}
          </div>
        </section>
      ) : null}
    </>
  );
}
